<template>
  <div class="result-card">
    <div v-for="(group, gIdx) in groupedSnapshot" :key="gIdx" class="section">
      <!-- Title -->
      <h6
        class="section-title clickable"
        @click="toggleSection(gIdx)"
        :aria-expanded="!isCollapsed(gIdx)"
      >
        <span class="chevron" :class="{ open: !isCollapsed(gIdx) }">▸</span>
        <span class="title-text">
          {{ group.section || (($t && $t('risk.template.no_section')) || 'General') }}
        </span>
      </h6>

      <!-- Content -->
      <ul class="qa-list" v-show="!isCollapsed(gIdx)">
        <li
          v-for="field in group.items"
          :key="field.item_id || field.id || field.key || field.name"
          class="qa-row"
        >
          <div class="q">{{ field.name }}</div>
          <div class="a">{{ displayAnswer(field) }}</div>
          <div v-if="showComments" class="c" :title="displayComment(field)">{{ displayComment(field) }}</div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "@vue/composition-api";

export default {
  name: "RiskAnswersView",
  props: {
    snapshot: { type: Array, default: () => [] },              // fields (template_snapshot or templateData)
    answers:  { type: [Object, Array], default: () => ({}) },  // raw data (object/array)
    alarmsOnly: { type: Boolean, default: false },
    showComments: { type: Boolean, default: true },
  },
  setup(props) {
    //colapsed by section
    const collapsedSet = ref(new Set());
    const isCollapsed = (idx) => collapsedSet.value.has(idx);
    const toggleSection = (idx) => {
      const next = new Set(collapsedSet.value);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      collapsedSet.value = next;
    };

    //robust index values plus comments
    const buildIndexes = (raw) => {
      const values = {};
      const comments = {};

      const put = (map, k, v) => {
        if (k == null) return;
        const ks = String(k);
        map[ks] = v;
        const parts = ks.split(":");
        if (parts.length > 1) map[parts[parts.length - 1]] = v; //alias with out prefix
      };

      const extractVal = (o) => {
        if (o == null) return o;
        if (typeof o !== "object") return o;
        if (Array.isArray(o)) return o;
        return o.value ?? o.answer ?? o.text ?? o.selected ?? o.data ?? o.values ?? o;
      };

      const extractComment = (o) => {
        if (o == null || typeof o !== "object") return null;
        return o.comment ?? o.comments ?? o.note ?? o.notes ?? o.review_comment ?? o.observation ?? null;
      };

      const putWithAliases = (baseKey, val, cmt) => {
        if (val !== undefined) put(values, baseKey, val);
        if (cmt != null && cmt !== "") put(comments, baseKey, cmt);
      };

      const fromArray = (arr) => {
        arr.forEach((e) => {
          if (!e) return;
          const key = e.item_id ?? e.field ?? e.key ?? e.id ?? e.name;
          const val = extractVal(e);
          const cmt = extractComment(e);
          putWithAliases(key, val, cmt);
        });
      };

      if (Array.isArray(raw)) {
        fromArray(raw);
      } else if (raw && typeof raw === "object") {
        if (Array.isArray(raw.answers)) fromArray(raw.answers);
        if (Array.isArray(raw.data)) fromArray(raw.data);

        Object.entries(raw).forEach(([k, v]) => {
          if (k === "answers" || k === "data") return;

          const lower = k.toLowerCase().trim();
          const underscore = ["_comment", "_comments", "_note", "_notes", "_review_comment", "_observation"]
            .find(s => lower.endsWith(s));
          const dashed = [" - comment", " - comments", " - comentario", " - comentarios"]
            .find(s => lower.endsWith(s));

          if (underscore) {
            const base = k.slice(0, -underscore.length);
            const cmtVal = typeof v === "object" ? (v?.text ?? v?.comment ?? v?.value ?? v) : v;
            putWithAliases(base, values[base], cmtVal);
            return;
          }
          if (dashed) {
            const base = k.slice(0, -dashed.length);
            const cmtVal = typeof v === "object" ? (v?.text ?? v?.comment ?? v?.value ?? v) : v;
            putWithAliases(base, values[base], cmtVal);
            return;
          }

          if (typeof v === "object" && !Array.isArray(v)) {
            const val = extractVal(v);
            const cmt = extractComment(v);
            putWithAliases(k, val, cmt);
          } else {
            putWithAliases(k, v, null);
          }
        });
      }

      return { values, comments };
    };

    const indexes = computed(() => buildIndexes(props.answers ?? {}));
    const answersIndex = computed(() => indexes.value.values || {});
    const commentsIndex = computed(() => indexes.value.comments || {});

    //group by secction and alarm filter
    const filteredSnapshot = computed(() =>
      (props.snapshot || []).filter(f => !props.alarmsOnly || f?.options?.alarms === true)
    );

    const groupedSnapshot = computed(() => {
      const groups = new Map();
      filteredSnapshot.value.forEach((f) => {
        const key = f.section || "";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(f);
      });
      return Array.from(groups.entries()).map(([section, items]) => ({
        section,
        items,
      }));
    });

    //visualization helpers
    const mapOptionLabel = (field, val) => {
      const opts = field?.options?.fieldTypeCfg?.data || [];
      const by = (o) => o?.value ?? o?.id ?? o?.key ?? o?.name;
      const hit = opts.find((o) => String(by(o)) === String(val));
      return hit?.name ?? String(val);
    };

    const pickCandidateKeys = (field) => {
      const out = [];
      const push = (v) => { if (v != null && v !== "") out.push(String(v)); };
      push(field.item_id);
      if (field.item_id) {
        const s = String(field.item_id);
        const parts = s.split(":");
        if (parts.length > 1) push(parts[parts.length - 1]);
      }
      push(field.id);
      push(field.key);
      push(field.name); //case "Question - comment"
      return Array.from(new Set(out));
    };

    const getFromIndex = (map, field) => {
      const candidates = pickCandidateKeys(field);
      for (const k of candidates) {
        if (Object.prototype.hasOwnProperty.call(map, k)) return map[k];
      }
      return undefined;
    };

    const displayAnswer = (field) => {
      const idx = answersIndex.value || {};
      const raw = getFromIndex(idx, field);
      if (raw == null || raw === "") return "—";

      const val = raw?.value ?? raw?.text ?? raw;

      if (Array.isArray(val)) {
        if (val.length === 0) return "—";
        return val.map((v) => mapOptionLabel(field, v?.value ?? v)).join(", ");
      }

      if (typeof val === "object") {
        if (val.name) return String(val.name);
        if (val.label) return String(val.label);
        if (val.value != null) return String(val.value);
        try { return JSON.stringify(val); } catch { return String(val); }
      }

      if (field?.options?.fieldTypeCfg?.data) {
        return mapOptionLabel(field, val);
      }

      return String(val);
    };

    const formatComment = (v) => {
      if (v == null || v === "") return "";
      if (Array.isArray(v)) return v.join(", ");
      if (typeof v === "object") return v.text ?? v.comment ?? v.value ?? JSON.stringify(v);
      return String(v);
    };

    const displayComment = (field) => {
      const idx = commentsIndex.value || {};
      const raw = getFromIndex(idx, field);
      return formatComment(raw);
    };

    return {
      groupedSnapshot,
      displayAnswer,
      displayComment,
      isCollapsed,
      toggleSection,
    };
  },
};
</script>

<style scoped>
.result-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 10px;
  padding: 12px;
}
.section + .section { margin-top: 12px; }

/* section title clickable*/
.section-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 6px;
  border-bottom: 1px solid #eef1f4;
  padding-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.section-title.clickable { cursor: pointer; }
.section-title .title-text { flex: 1 1 auto; }

/* click icon small and with rotation */
.chevron {
  font-size: 0.8rem;
  line-height: 1;
  display: inline-block;
  transition: transform 160ms ease;
  transform-origin: center;
}
.chevron.open { transform: rotate(90deg); }

/* QA: question | answer | comment */
.qa-list { list-style: none; padding: 0; margin: 0; }
.qa-row {
  display: grid;
  grid-template-columns: 1fr 1.2fr 1fr;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #edf0f3;
}
.qa-row:last-child { border-bottom: 0; }

.q { color: #334155; font-weight: 500; }
.a { color: #111827; }
.c { color: #64748b; white-space: normal; }

.dark-layout .result-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.dark-layout .result-card .section-title {
  color: #d0d2d6;
  border-bottom-color: rgba(255, 255, 255, 0.12);
}
.dark-layout .qa-row { border-bottom-color: rgba(255, 255, 255, 0.08); }
.dark-layout .q { color: #b4b7bd; }
.dark-layout .a { color: #ffffff; }
.dark-layout .c { color: #9aa5b1; }
</style>
